/** Посадочные страницы для основных сценариев планировщика. */
import "./scenarioLanding.css";
import { Link } from "react-router-dom";
import { routerPaths } from "@/app/routerPaths";
import { AnalyticsGoal, trackGoal } from "@/shared/lib/analytics";
import { UIKit } from "@/shared/ui";
import { ElevationArt, LandingFooter } from "@/widgets/landing";
import {
	ElevationsDetails,
	FloorPlanDetails,
	Interior3dDetails,
	KitchenDetails,
	RoomDetails,
	WardrobeDetails,
} from "./ScenarioDetails";

const planner = {
	label: "Планировщик помещений",
	title: "Планировка квартиры онлайн",
	intro:
		"Начертите комнаты по размерам, расставьте мебель и посмотрите, как будет выглядеть пространство в 3D.",
	action: "Создать планировку",
	to: routerPaths.planner,
	goal: AnalyticsGoal.PlannerCtaClicked,
	steps: [
		{
			title: "Начертите комнаты",
			text: "Задайте размеры стен, добавьте двери и окна. Площади и размерные линии появятся на плане.",
		},
		{
			title: "Расставьте мебель",
			text: "Разместите предметы из каталога, проверьте проходы и подберите отделку.",
		},
		{
			title: "Посмотрите результат",
			text: "Откройте 3D-вид, развёртки стен и смету по вашему чертежу.",
		},
	],
	nextTitle: "Кухня по вашим размерам",
	nextText: "Соберите кухню в конструкторе и добавьте её в проект.",
	nextTo: routerPaths.kitchenLanding,
	nextLabel: "Конструктор кухни",
};

const kitchen = {
	label: "Конструктор мебели",
	title: "Конструктор кухни онлайн",
	intro:
		"Соберите кухню под размеры помещения: выберите форму, расставьте секции и посмотрите результат в 3D.",
	action: "Попробовать конструктор кухни",
	to: `${routerPaths.furnitureGenerator}?type=kitchen`,
	goal: AnalyticsGoal.KitchenCtaClicked,
	steps: [
		{
			title: "Выберите форму",
			text: "Прямая, угловая или П-образная кухня, с островом или без него.",
		},
		{
			title: "Настройте секции",
			text: "Задайте размеры, выберите фасады, столешницу и расположение техники.",
		},
		{
			title: "Добавьте в проект",
			text: "Сохраните кухню в каталоге и разместите её на плане квартиры.",
		},
	],
	nextTitle: "Начните с плана квартиры",
	nextText: "Начертите помещение и проверьте, как кухня впишется в пространство.",
	nextTo: routerPaths.floorPlanLanding,
	nextLabel: "Планировка квартиры",
};

const room = {
	label: "Планировщик помещений",
	title: "Планировка комнаты онлайн",
	intro:
		"Создайте план комнаты по размерам, добавьте окна и двери и подберите расположение мебели.",
	action: "Создать план комнаты",
	to: routerPaths.editor2d,
	goal: AnalyticsGoal.PlannerCtaClicked,
	steps: [
		{
			title: "Задайте размеры",
			text: "Начертите стены комнаты или создайте её по заданным размерам.",
		},
		{ title: "Добавьте проёмы", text: "Разместите двери и окна на стенах и уточните их размеры." },
		{
			title: "Расставьте мебель",
			text: "Подберите предметы из каталога и проверьте расстояния до стен.",
		},
	],
	nextTitle: "Посмотрите комнату в 3D",
	nextText: "Переключитесь на объёмный вид и оцените расстановку и отделку.",
	nextTo: routerPaths.interior3dLanding,
	nextLabel: "3D-планировка",
};

const interior3d = {
	label: "3D-планировщик",
	title: "Планировка интерьера в 3D",
	intro:
		"Перейдите от чертежа к объёмному виду: расставьте мебель, подберите отделку и рассмотрите помещение.",
	action: "Открыть 3D-планировщик",
	to: routerPaths.editor3d,
	goal: AnalyticsGoal.PlannerCtaClicked,
	steps: [
		{ title: "Создайте план", text: "Начертите помещение в 2D или откройте готовый проект." },
		{
			title: "Добавьте предметы",
			text: "Разместите мебель и сантехнику и выберите отделку поверхностей.",
		},
		{
			title: "Рассмотрите в 3D",
			text: "Меняйте ракурс и проверяйте, как элементы выглядят вместе.",
		},
	],
	nextTitle: "Начните с размеров комнаты",
	nextText: "Подготовьте точный план, чтобы расставить предметы в нужном пространстве.",
	nextTo: routerPaths.roomLanding,
	nextLabel: "Планировка комнаты",
};

const wardrobe = {
	label: "Конструктор мебели",
	title: "Конструктор шкафа онлайн",
	intro: "Соберите шкаф по своим размерам, настройте секции и посмотрите результат в 3D.",
	action: "Попробовать конструктор шкафа",
	to: `${routerPaths.furnitureGenerator}?type=wardrobe`,
	goal: AnalyticsGoal.WardrobeCtaClicked,
	steps: [
		{
			title: "Задайте габариты",
			text: "Укажите ширину, высоту и глубину шкафа под своё помещение.",
		},
		{
			title: "Настройте наполнение",
			text: "Разделите шкаф на секции и выберите их внутреннее наполнение.",
		},
		{ title: "Добавьте в проект", text: "Сохраните шкаф в каталоге и разместите его на плане." },
	],
	nextTitle: "Проверьте шкаф на плане",
	nextText: "Создайте комнату по размерам и найдите подходящее место для шкафа.",
	nextTo: routerPaths.roomLanding,
	nextLabel: "Планировка комнаты",
};

const elevations = {
	label: "Рабочие чертежи",
	title: "Развёртки стен онлайн",
	intro: "Получите виды стен по чертежу помещения с размерами, проёмами и размещёнными объектами.",
	action: "Открыть развёртки стен",
	to: routerPaths.editorElevations,
	goal: AnalyticsGoal.PlannerCtaClicked,
	steps: [
		{ title: "Начертите помещение", text: "Постройте стены и добавьте двери и окна на плане." },
		{
			title: "Разместите объекты",
			text: "Добавьте на стены розетки, выключатели и другие предметы.",
		},
		{
			title: "Откройте развёртки",
			text: "Посмотрите стены по помещениям с размерами и расположением объектов.",
		},
	],
	nextTitle: "Сначала создайте план",
	nextText: "Развёртки рассчитываются по стенам и объектам вашего проекта.",
	nextTo: routerPaths.floorPlanLanding,
	nextLabel: "Планировка квартиры",
};

const estimate = {
	label: "Расчёт ремонта",
	title: "Смета ремонта по плану",
	intro: "Получите предварительный расчёт объёмов работ и материалов по вашему чертежу.",
	action: "Открыть смету",
	to: routerPaths.editorEstimate,
	goal: AnalyticsGoal.PlannerCtaClicked,
	steps: [
		{
			title: "Создайте чертёж",
			text: "Начертите помещения и добавьте работы и материалы в проект.",
		},
		{
			title: "Проверьте расчёт",
			text: "Посмотрите объёмы и состав позиций с разбивкой по помещениям.",
		},
		{
			title: "Уточните цены",
			text: "Измените примерные цены и выгрузите результат в Excel или PDF.",
		},
	],
	nextTitle: "Начните с плана квартиры",
	nextText: "Размеры и выбранные материалы станут основой предварительной сметы.",
	nextTo: routerPaths.floorPlanLanding,
	nextLabel: "Планировка квартиры",
};

type Scenario = Omit<typeof planner, "to" | "nextTo"> & { to: string; nextTo: string };

const questions: Record<string, { question: string; answer: string }[]> = {
	[planner.title]: [
		{
			question: "Можно ли начать без регистрации?",
			answer:
				"Да. Гость может открыть демо или пустой временный проект. Для сохранения собственного проекта нужен аккаунт.",
		},
		{
			question: "Можно ли обвести существующий план?",
			answer:
				"Да. Загрузите изображение как подложку, задайте масштаб по известному размеру и начертите стены поверх него.",
		},
	],
	[kitchen.title]: [
		{
			question: "Можно ли изменить отдельную секцию кухни?",
			answer: "Да. У верхних и нижних секций свои настройки размеров и фасадов.",
		},
		{
			question: "Как поставить кухню на план?",
			answer:
				"Сохраните её в каталог своей мебели и выберите оттуда в редакторе. Для сохранения нужен аккаунт.",
		},
	],
	[room.title]: [
		{
			question: "Как задать комнату точного размера?",
			answer: "Используйте создание комнаты по размерам или начертите стены и задайте их длины.",
		},
		{
			question: "Можно ли проверить расстояние до мебели?",
			answer: "Да. У выбранного предмета можно показать расстояния до стен и других предметов.",
		},
	],
	[interior3d.title]: [
		{
			question: "Нужно ли строить помещение в 3D заново?",
			answer: "Нет. Сцена строится из того же плана, что открыт в 2D.",
		},
		{
			question: "Можно ли менять отделку и предметы?",
			answer:
				"Да. В 3D можно выбирать и перемещать предметы, а также подбирать отделку поверхностей.",
		},
	],
	[wardrobe.title]: [
		{
			question: "Можно ли вернуться к редактированию шкафа?",
			answer: "Да. Поставленный шкаф можно открыть в конструкторе и изменить.",
		},
		{
			question: "Где сохраняется собранный шкаф?",
			answer:
				"После добавления он появляется в разделе каталога «Моя мебель». Для сохранения нужен аккаунт.",
		},
	],
	[elevations.title]: [
		{
			question: "Нужно ли рисовать развёртки вручную?",
			answer: "Нет. Они рассчитываются по стенам, проёмам и объектам чертежа.",
		},
		{
			question: "Какие розетки видны на развёртке?",
			answer: "Точки, установленные на той стороне стены, которая обращена в выбранную комнату.",
		},
	],
	[estimate.title]: [
		{
			question: "Откуда берутся количества?",
			answer:
				"Расчётные объёмы берутся из плана; у позиции доступно объяснение расчёта. Количество можно изменить.",
		},
		{
			question: "Можно ли использовать свои цены?",
			answer:
				"Да. Можно задать собственный прайс и добавить позиции, которых нет на чертеже. Примерные цены не заменяют расчёт подрядчика.",
		},
	],
};

/** Состав и ограничения предварительного расчёта ремонта. */
const EstimateDetails = () => (
	<section className="border-t border-border bg-bg text-ink">
		<div className="mx-auto grid max-w-7xl gap-10 px-4 py-16 sm:px-8 lg:grid-cols-2 lg:gap-20 lg:py-20">
			<div>
				<UIKit.Text as="span" className="text-xs font-semibold text-accent">
					РАСЧЁТ ПО ЧЕРТЕЖУ
				</UIKit.Text>
				<h2 className="mt-3 text-3xl font-bold">Что попадёт в смету</h2>
				<UIKit.Text className="mt-5 max-w-lg text-base leading-relaxed text-muted">
					Площади, длины и количество берутся из проекта. У каждой расчётной позиции можно
					посмотреть, как получено количество, и при необходимости изменить его.
				</UIKit.Text>
			</div>
			<dl className="border-t border-border">
				{[
					["Объёмы", "Работы и материалы, рассчитанные по плану и выбранной отделке."],
					["Цены", "Примерные расценки по региону или ваш собственный прайс."],
					[
						"Дополнения",
						"Свои позиции, которых нет на чертеже, например доставка или вывоз мусора.",
					],
					["Результат", "Разбивка по помещениям или разделам и выгрузка в Excel и PDF."],
				].map(([title, text]) => (
					<div
						key={title}
						className="grid gap-1 border-b border-border py-4 sm:grid-cols-3 sm:gap-4"
					>
						<dt className="font-semibold">{title}</dt>
						<dd className="text-sm leading-relaxed text-muted sm:col-span-2">{text}</dd>
					</div>
				))}
			</dl>
			<UIKit.Text className="text-sm leading-relaxed text-muted lg:col-start-2">
				Примерные цены помогают оценить бюджет, но не заменяют смету подрядчика.
			</UIKit.Text>
		</div>
	</section>
);

/** Страница сценария с переходом непосредственно к соответствующему инструменту. */
const ScenarioLanding = ({
	scenario,
	isPlanner,
	children,
	reverse = false,
	elevationArt = false,
}: {
	scenario: Scenario;
	isPlanner: boolean;
	children?: React.ReactNode;
	reverse?: boolean;
	elevationArt?: boolean;
}) => (
	<main className="min-h-0 flex-1 overflow-y-auto bg-surface">
		<section
			className={`scenario-hero${reverse ? " scenario-hero--reverse" : ""}`}
			aria-labelledby="scenario-title"
			style={elevationArt ? undefined : { backgroundImage: 'url("/landing/hero-3d.webp")' }}
		>
			{elevationArt && (
				<div className="scenario-hero-drawing">
					<ElevationArt />
				</div>
			)}
			<div className="scenario-hero-inner mx-auto max-w-7xl px-4 sm:px-8">
				<UIKit.Text as="span" className="scenario-eyebrow text-xs font-semibold">
					GRID PLANNER / {scenario.label}
				</UIKit.Text>
				<h1
					id="scenario-title"
					className="mt-5 text-4xl leading-tight font-bold sm:text-5xl lg:text-6xl"
				>
					{scenario.title}
				</h1>
				<UIKit.Text className="mt-5 max-w-lg text-base leading-relaxed sm:text-lg">
					{scenario.intro}
				</UIKit.Text>
				<Link
					to={scenario.to}
					onClick={() => trackGoal(scenario.goal)}
					className="scenario-action mt-7 inline-flex min-h-12 items-center justify-center px-6 text-sm font-semibold"
				>
					{isPlanner ? (
						<>
							<UIKit.Text as="span" className="md:hidden">
								Открыть проект
							</UIKit.Text>
							<UIKit.Text as="span" className="hidden md:inline">
								{scenario.action}
							</UIKit.Text>
						</>
					) : (
						scenario.action
					)}
				</Link>
				{isPlanner ? (
					<UIKit.Text className="mt-3 max-w-sm text-xs leading-relaxed md:hidden">
						На телефоне доступен просмотр. Чтобы редактировать план, откройте его на компьютере.
					</UIKit.Text>
				) : (
					<UIKit.Text className="mt-3 max-w-sm text-xs leading-relaxed">
						Для сохранения мебели в каталог нужен аккаунт.
					</UIKit.Text>
				)}
			</div>
		</section>
		{children}
		<section id="how" className="bg-surface text-ink">
			<div className="mx-auto max-w-7xl px-4 py-16 sm:px-8 lg:py-20">
				<UIKit.Text as="span" className="text-xs font-semibold text-muted">
					ОТ ИДЕИ К ПРОЕКТУ
				</UIKit.Text>
				<h2 className="mt-3 text-3xl font-bold sm:text-4xl">Три шага к результату</h2>
				<ol className="mt-8 grid gap-8 md:grid-cols-3 md:gap-10">
					{scenario.steps.map((step, index) => (
						<li key={step.title} className="border-t border-border pt-5">
							<UIKit.Text as="span" className="text-sm font-semibold text-accent">
								0{index + 1}
							</UIKit.Text>
							<h3 className="mt-3 text-xl font-semibold">{step.title}</h3>
							<UIKit.Text className="mt-3 text-sm leading-relaxed text-muted">
								{step.text}
							</UIKit.Text>
						</li>
					))}
				</ol>
			</div>
		</section>
		<section className="border-t border-border bg-surface text-ink">
			<div className="mx-auto grid max-w-7xl gap-8 px-4 py-16 sm:px-8 lg:grid-cols-3 lg:gap-16">
				<h2 className="text-2xl font-bold">Частые вопросы</h2>
				<div className="lg:col-span-2">
					{questions[scenario.title].map(({ question, answer }) => (
						<details key={question} className="border-t border-border py-5">
							<summary className="cursor-pointer font-semibold">{question}</summary>
							<UIKit.Text className="mt-3 max-w-2xl text-sm leading-relaxed text-muted">
								{answer}
							</UIKit.Text>
						</details>
					))}
				</div>
			</div>
		</section>
		<section id="features" className="border-t border-border bg-bg text-ink">
			<div className="mx-auto flex max-w-7xl flex-col items-start gap-5 px-4 py-14 sm:px-8 md:flex-row md:items-center md:justify-between">
				<div>
					<h2 className="text-2xl font-bold sm:text-3xl">{scenario.nextTitle}</h2>
					<UIKit.Text className="mt-2 max-w-xl text-sm leading-relaxed text-muted">
						{scenario.nextText}
					</UIKit.Text>
				</div>
				<Link
					className="shrink-0 border-b border-accent pb-1 text-sm font-semibold text-accent"
					to={scenario.nextTo}
				>
					{scenario.nextLabel} →
				</Link>
			</div>
		</section>
		<LandingFooter />
	</main>
);

/** Посадочная страница создания планировки. */
export const FloorPlanLandingPage = () => (
	<ScenarioLanding scenario={planner} isPlanner>
		<FloorPlanDetails />
	</ScenarioLanding>
);

/** Посадочная страница конструктора кухни. */
export const KitchenLandingPage = () => (
	<ScenarioLanding scenario={kitchen} isPlanner={false} reverse>
		<KitchenDetails />
	</ScenarioLanding>
);

/** Посадочная страница планировки комнаты. */
export const RoomLandingPage = () => (
	<ScenarioLanding scenario={room} isPlanner reverse>
		<RoomDetails />
	</ScenarioLanding>
);

/** Посадочная страница 3D-планировки. */
export const Interior3dLandingPage = () => (
	<ScenarioLanding scenario={interior3d} isPlanner>
		<Interior3dDetails />
	</ScenarioLanding>
);

/** Посадочная страница конструктора шкафа. */
export const WardrobeLandingPage = () => (
	<ScenarioLanding scenario={wardrobe} isPlanner={false} reverse>
		<WardrobeDetails />
	</ScenarioLanding>
);

/** Посадочная страница развёрток стен. */
export const ElevationsLandingPage = () => (
	<ScenarioLanding scenario={elevations} isPlanner elevationArt>
		<ElevationsDetails />
	</ScenarioLanding>
);

/** Посадочная страница предварительной сметы. */
export const EstimateLandingPage = () => (
	<ScenarioLanding scenario={estimate} isPlanner reverse>
		<EstimateDetails />
	</ScenarioLanding>
);
